import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, BookOpen, TrendingUp, AlertTriangle, CheckCircle, Lock, Loader2, PlayCircle, Building2, FileText, Lightbulb, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const InvestmentsPage = () => {
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/investments/types`);
      setInvestments(response.data.types);
      if (response.data.types.length > 0) {
        setSelectedType(response.data.types[0]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    if (risk.includes('Muito Baixo')) return 'text-[#00FF94] bg-[#00FF94]/10';
    if (risk.includes('Baixo')) return 'text-green-400 bg-green-400/10';
    if (risk.includes('Médio')) return 'text-yellow-400 bg-yellow-400/10';
    return 'text-red-400 bg-red-400/10';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14"
              onClick={() => navigate('/')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-evite-dividas.png" alt="Evite Dívidas" className="w-12 h-12 object-contain" />
              <span className="font-bold text-white">Evite Dívidas</span>
            </div>
            <Button 
              variant="ghost" 
              className="text-[#00FF94]"
              onClick={() => navigate('/simulator')}
              data-testid="simulator-btn"
            >
              Simulador
            </Button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Educacao Financeira
            </span>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Investimentos para Iniciantes
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Aprenda sobre os principais tipos de investimentos seguros para começar a fazer seu dinheiro render
            </p>
          </motion.div>

          {/* Investment Types Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left sidebar - Investment list */}
            <motion.div 
              className="lg:col-span-1 space-y-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Tipos de Investimento
              </h3>
              {investments.map((inv) => (
                <button
                  key={inv.name}
                  onClick={() => setSelectedType(inv)}
                  className={`w-full text-left p-4 rounded-xl transition-all ${
                    selectedType?.name === inv.name
                      ? 'bg-[#00FF94]/10 border border-[#00FF94]/30'
                      : 'bg-[#0A0A0A] border border-white/5 hover:border-white/10'
                  }`}
                  data-testid={`investment-type-${inv.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${selectedType?.name === inv.name ? 'text-[#00FF94]' : 'text-white'}`}>
                      {inv.name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${getRiskColor(inv.risk_level)}`}>
                      {inv.risk_level}
                    </span>
                  </div>
                </button>
              ))}
            </motion.div>

            {/* Main content - Selected investment details */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              key={selectedType?.name}
            >
              {selectedType && (
                <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">{selectedType.name}</h2>
                      <span className={`text-sm px-3 py-1 rounded-full ${getRiskColor(selectedType.risk_level)}`}>
                        Risco: {selectedType.risk_level}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Rendimento estimado</p>
                      <p className="text-2xl font-bold text-[#00FF94] font-mono">
                        {(selectedType.annual_rate * 100).toFixed(1)}% a.a.
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-8 leading-relaxed">
                    {selectedType.description}
                  </p>

                  <Tabs defaultValue="info" className="w-full">
                    <TabsList className="bg-[#121212] border border-white/5 mb-6 flex-wrap h-auto gap-1 p-1">
                      <TabsTrigger value="info" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]">
                        Informacoes
                      </TabsTrigger>
                      <TabsTrigger value="pros" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]">
                        Vantagens
                      </TabsTrigger>
                      <TabsTrigger value="cons" className="data-[state=active]:bg-[#00FF94]/20 data-[state=active]:text-[#00FF94]">
                        Desvantagens
                      </TabsTrigger>
                      <TabsTrigger value="start" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Como Começar
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="info" className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-[#121212] rounded-xl p-4">
                          <p className="text-sm text-gray-500 mb-1">Liquidez</p>
                          <p className="text-white font-medium">{selectedType.liquidity}</p>
                        </div>
                        <div className="bg-[#121212] rounded-xl p-4">
                          <p className="text-sm text-gray-500 mb-1">Valor Minimo</p>
                          <p className="text-white font-medium">{selectedType.minimum}</p>
                        </div>
                      </div>
                      <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-xl p-4">
                        <p className="text-sm text-gray-400 mb-2">Ideal para:</p>
                        <p className="text-white">{selectedType.ideal_for}</p>
                      </div>
                    </TabsContent>

                    <TabsContent value="pros" className="space-y-3">
                      {selectedType.pros.map((pro, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-[#121212] rounded-xl">
                          <CheckCircle className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{pro}</span>
                        </div>
                      ))}
                    </TabsContent>

                    <TabsContent value="cons" className="space-y-3">
                      {selectedType.cons.map((con, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-[#121212] rounded-xl">
                          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{con}</span>
                        </div>
                      ))}
                    </TabsContent>

                    <TabsContent value="start" className="space-y-6">
                      {selectedType.how_to_start && (
                        <>
                          {/* Difficulty and Time */}
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-[#121212] rounded-xl p-4 flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/20 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-[#00FF94]" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Dificuldade</p>
                                <p className="text-white font-medium">{selectedType.how_to_start.difficulty}</p>
                              </div>
                            </div>
                            <div className="bg-[#121212] rounded-xl p-4 flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-amber-400" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Tempo para abrir</p>
                                <p className="text-white font-medium">{selectedType.how_to_start.time_to_open}</p>
                              </div>
                            </div>
                          </div>

                          {/* Steps */}
                          <div className="bg-gradient-to-br from-[#00FF94]/5 to-transparent border border-[#00FF94]/20 rounded-xl p-5">
                            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                              <PlayCircle className="w-5 h-5 text-[#00FF94]" />
                              Passo a Passo
                            </h4>
                            <div className="space-y-3">
                              {selectedType.how_to_start.steps.map((step, i) => (
                                <div key={i} className="flex items-start gap-3">
                                  <div className="w-6 h-6 rounded-full bg-[#00FF94] text-black text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                    {i + 1}
                                  </div>
                                  <span className="text-gray-300">{step}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Documents */}
                          <div className="bg-[#121212] rounded-xl p-5">
                            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                              <FileText className="w-5 h-5 text-gray-400" />
                              Documentos Necessarios
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedType.how_to_start.documents_needed.map((doc, i) => (
                                <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300">
                                  {doc}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Recommended Banks */}
                          <div className="bg-[#121212] rounded-xl p-5">
                            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                              <Building2 className="w-5 h-5 text-gray-400" />
                              Onde Investir (Recomendados)
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedType.how_to_start.recommended_banks.map((bank, i) => (
                                <span key={i} className="px-3 py-1.5 bg-[#00FF94]/10 border border-[#00FF94]/20 rounded-full text-sm text-[#00FF94] font-medium">
                                  {bank}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Tips */}
                          <div className="bg-amber-500/5 border border-amber-400/20 rounded-xl p-5">
                            <h4 className="text-amber-400 font-semibold mb-4 flex items-center gap-2">
                              <Lightbulb className="w-5 h-5" />
                              Dicas Importantes
                            </h4>
                            <div className="space-y-3">
                              {selectedType.how_to_start.tips.map((tip, i) => (
                                <div key={i} className="flex items-start gap-3">
                                  <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-1" />
                                  <span className="text-gray-300 text-sm">{tip}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </TabsContent>
                  </Tabs>

                  <div className="mt-8 pt-6 border-t border-white/5">
                    <Button 
                      className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold"
                      onClick={() => navigate('/simulator')}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Simular Investimento
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Disclaimer */}
          <motion.div
            className="mt-12 p-6 bg-[#0A0A0A] border border-white/5 rounded-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-gray-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-white mb-2">Aviso Importante</h3>
                <p className="text-sm text-gray-500">
                  As informações apresentadas são apenas educativas e não constituem recomendação de investimento. 
                  Rentabilidades passadas não garantem rentabilidades futuras. Antes de investir, avalie seu perfil 
                  de investidor e consulte um profissional certificado.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Quick Navigation */}
      <QuickNav />
    </div>
  );
};

export default InvestmentsPage;
